import { useCallback, useEffect, useRef, useState } from 'react';

import {
	type ChatThreadKind,
	type IOutboxItem,
	type IOwnershipContext,
	type ISupportMessage,
	type ISupportReplyContext,
} from '../interface/support-chat';
import { isDocumentAttachment } from '../lib/chat-attachment';
import { type ChatThreadRef, localThreadId } from '../lib/chat-thread-ref';
import { buildThumbnailDataUrl, compressForUpload } from '../lib/image-compress';
import {
	deleteSupportMessage,
	editSupportMessage,
	fetchSupportMessages,
	isOwnMessage,
	markSupportRead,
	normalizeMessage,
	sendSupportMessage,
	startPeerThread,
	startSupportThread,
	uploadSupportAttachment,
} from '../lib/support-api';
import { hasExhaustedAttempts, isDue, nextAttemptAt } from '../lib/support-backoff';
import { mintClientId } from '../lib/support-client-id';
import { upsertByClientIdThenId, upsertMany } from '../lib/support-merge';
import {
	itemsForThread,
	needsReattachment,
	OutboxStorageFullError,
	pendingItems,
	readOutbox,
	readThreadCache,
	removeOutboxItem,
	subscribeSupportChatChanged,
	upsertOutboxItem,
	writeThreadCache,
} from '../lib/support-outbox';
import { type SupportConnectionStatus, threadKeyOf } from '../lib/support-signalr';
import { canEmitTyping, hasTypingExpired, shouldEmitStop, shouldEmitTyping } from '../lib/support-typing';
import { type ISupportSession } from './useSupportSession';

/** Poll cadence. Slow while the panel is closed — that pass only feeds the unread badge. */
const POLL_OPEN_MS = 15_000;
const POLL_CLOSED_MS = 90_000;

/**
 * Polling does not stop when the socket connects, it slows down.
 *
 * A live socket is not proof of delivery: a group join can fail silently, a broadcast can be
 * dropped by a proxy, and the connection can look Connected while the server has forgotten
 * the group. A slow poll is the reconciliation pass that makes those failures self-healing
 * instead of permanent — and it costs one request every two minutes.
 */
const POLL_LIVE_MS = 120_000;

/** Flush attempts are cheap; the backoff on each item decides what actually goes. */
const FLUSH_TICK_MS = 5_000;

/** How often an open typing burst is checked for an idle stop. */
const TYPING_STOP_TICK_MS = 1_000;

/**
 * Whether the support service can be reached, and if not, why.
 *
 * <p>This exists because a refused `POST /threads` used to be indistinguishable from "still
 * sending": the failure was swallowed, the outbox item stayed `queued`, and the bubble showed
 * a clock forever. An agent reporting a blocked polling unit had no way to learn their message
 * was going nowhere.</p>
 *
 * <p>There is deliberately no `offline` member — that is `navigator.onLine`'s question and it
 * has its own banner. These are answers about the SERVER.</p>
 */
export type SupportReach = 'unknown' | 'ok' | 'unavailable' | 'forbidden' | 'signedOut' | 'failed';

const reachFromStatus = (status?: number): SupportReach => {
	// 404 here is the `Support:Enabled` flag, not a missing record: every route on
	// SupportChatController opens with a guard that returns a bare NotFound() when it is off.
	if (status === 404 || status === 405) return 'unavailable';
	if (status === 403) return 'forbidden';
	if (status === 401) return 'signedOut';
	return 'failed';
};

const statusOf = (error: unknown): number | undefined => (error as { response?: { status?: number } })?.response?.status;

export interface IChatThreadState {
	/** Which kind of conversation this is. Decides copy, ownership fallbacks and the opener. */
	kind: ChatThreadKind;
	/** The client-side id (`support`, `peer:{election}:{user}`) that keys caches and the outbox. */
	localId: string;
	/** The reader's core user id — what ownership is decided against. */
	me: number;
	/** The server's key, once the thread has been opened. */
	threadKey: string | null;
	messages: ISupportMessage[];
	unread: number;
	online: boolean;
	/** Socket state, so the panel can say "Reconnecting…" rather than looking frozen. */
	liveStatus: SupportConnectionStatus;
	/** True while the other party is composing. Expires on its own — see support-typing.ts. */
	otherIsTyping: boolean;
	/** Called on each keystroke; throttles internally. */
	notifyTyping: () => void;
	/**
	 * Client ids whose photo was lost to a reload.
	 *
	 * The outbox keeps the metadata and a thumbnail, but the File itself lived in memory. The
	 * message is NOT discarded and NOT silently sent without its photo — the agent is asked to
	 * pick it again, against the SAME clientId, so it keeps its place in the queue and its
	 * idempotency key.
	 */
	needsReattach: Set<string>;
	/** Persisted thumbnails by clientId, so a reloaded bubble still renders. */
	thumbnails: Record<string, string>;
	/** Re-attach a photo to an existing queued message. */
	reattach: (clientId: string, file: File) => void;
	/** Report that messages up to this sequence have been read. */
	reportRead: (upToSequence: number) => void;
	/** Edit a sent message. Resolves to an error string, or null on success. */
	editMessage: (messageId: string, body: string) => Promise<string | null>;
	/** Retract for everyone. Resolves to an error string, or null on success. */
	deleteMessage: (messageId: string) => Promise<string | null>;
	sending: boolean;
	storageFull: boolean;
	localPreviews: Record<string, string>;
	uploadPercents: Record<string, number>;
	ownClientIds: Set<string>;
	open: () => void;
	close: () => void;
	send: (body: string, file: File | null, replyTo?: ISupportReplyContext | null) => void;
	retry: (clientId: string) => void;
	pendingAttachmentCount: number;
	/** Whether the server can be reached for this thread. Drives the panel banner. */
	reach: SupportReach;
	/** Clear the start backoff and try to open the thread again, now. */
	retryThread: () => void;
	/**
	 * Whether the composer should refuse input.
	 *
	 * <p>Only on a peer thread the server has refused: a message to someone who is not your
	 * parent or child must not be queued, and a reassignment 403 does not fix itself in five
	 * seconds. The support thread never disables — its outbox posture is "keep the text".</p>
	 */
	composerDisabled: boolean;
}

export interface IChatThreadOptions {
	session: ISupportSession;
	/** The panel for THIS thread is on screen — drives the poll cadence. */
	isActive: boolean;
}

/**
 * Open (or re-open) the server thread behind a ref.
 *
 * <p>Both are idempotent upserts on a deterministic key. The support thread resolves to the
 * caller's own; a peer thread names the counterpart and the election, and the server orders
 * the key parent-first from api.core's proof — a child opening it and a parent opening it land
 * on the same document.</p>
 */
const openThread = async (ref: ChatThreadRef): Promise<{ key?: unknown }> => {
	if (ref.kind === 'support') return startSupportThread();
	return startPeerThread({ counterpartCoreUserId: ref.counterpartCoreUserId, electionId: ref.electionId });
};

/**
 * One conversation, end to end: thread resolution, transcript, outbox, socket, typing, reads.
 *
 * <p>Owns everything that is PER THREAD and nothing that is per session — the socket, the
 * heartbeat and the identity come in through {@link ISupportSession}, so a second conversation
 * costs a second instance of this hook and nothing else.</p>
 *
 * <p>Instances are meant to outlive panels. `useSupportChat` mounts one in the layout for the
 * support thread; peer threads are hosted the same way. Right-section panels unmount when
 * inactive, and a panel-owned engine would drop an in-flight send the moment an agent closes the
 * chat to upload a result sheet.</p>
 */
export const useChatThread = (ref: ChatThreadRef | null, { session, isActive }: IChatThreadOptions): IChatThreadState => {
	const { me, live, liveStatus, online } = session;
	const userId = me;
	const kind: ChatThreadKind = ref?.kind ?? 'support';
	const localId = ref ? localThreadId(ref) : '';

	const [threadKey, setThreadKey] = useState<string | null>(null);
	const [messages, setMessages] = useState<ISupportMessage[]>([]);
	const [sending, setSending] = useState(false);
	const [storageFull, setStorageFull] = useState(false);
	const [uploadPercents, setUploadPercents] = useState<Record<string, number>>({});
	const [otherIsTyping, setOtherIsTyping] = useState(false);
	const [typingBurst, setTypingBurst] = useState(false);
	const [reach, setReach] = useState<SupportReach>('unknown');

	const refRef = useRef<ChatThreadRef | null>(ref);
	refRef.current = ref;
	const liveRef = useRef(live);
	liveRef.current = live;
	const lastTypingEmitRef = useRef<number | null>(null);
	const lastKeystrokeAtRef = useRef<number | null>(null);
	const typingReceivedAtRef = useRef(0);

	// Blobs are NEVER persisted: localStorage is ~5MB, synchronous, string-only, and a 3MB
	// photo base64-encodes to ~4MB — it would blow the quota and evict the queued TEXT
	// messages alongside it. The compressed File lives here, in memory, for the run.
	const filesRef = useRef<Map<string, File>>(new Map());
	const previewsRef = useRef<Record<string, string>>({});
	// Per instance, so two conversations drain in parallel while one never drains twice.
	const flushingRef = useRef(false);
	const threadRef = useRef<string | null>(null);
	// Single-flight for POST threads. The mount poll, every 5s flush tick and each send all
	// call ensureThread(); without this they each fire their own request.
	const startingRef = useRef<Promise<string | null> | null>(null);
	const startAttemptsRef = useRef(0);
	const startBlockedUntilRef = useRef(0);
	// Held in a ref so the socket effects can call the latest refresh without listing it as a
	// dependency — depending on it would re-subscribe on every render.
	const refreshRef = useRef<(() => Promise<void>) | null>(null);

	const ownClientIds = useRef<Set<string>>(new Set());

	const ownership = useCallback((): IOwnershipContext => ({ me, kind, ownClientIds: ownClientIds.current }), [kind, me]);
	const own = useCallback((message: ISupportMessage) => isOwnMessage(message, ownership()), [ownership]);

	/** Paint from cache immediately, then let the network correct it. */
	useEffect(() => {
		if (!threadKey) return;
		const cached = readThreadCache(userId, threadKey);
		if (cached.length) setMessages((current) => upsertMany(current, cached));
	}, [threadKey]);

	/** Re-read when any tab-local writer touches the outbox (badge, hub row, panel). */
	useEffect(() => {
		if (!userId || !localId) return;
		return subscribeSupportChatChanged(userId, () => {
			setMessages((current) => upsertMany(current, outboxToMessages(itemsForThread(userId, localId), threadRef.current, me)));
		});
	}, [localId, me, userId]);

	/**
	 * Resolve this thread, opening it if it does not exist yet.
	 *
	 * <p>Never rejects — a support outage must not throw into the Dashboard layout. But it no
	 * longer stays SILENT either: the previous bare `catch { return null }` meant a 404 from a
	 * disabled feature flag, a 403 from a claim-poor token and a dead network were all
	 * indistinguishable from "still sending", forever, on a 5-second loop.</p>
	 *
	 * <p>`POST threads` is an idempotent upsert on a deterministic key, so calling this
	 * repeatedly is safe; the backoff exists to spare the shared rate limiter, not for
	 * correctness.</p>
	 */
	const ensureThread = useCallback(async (): Promise<string | null> => {
		if (threadRef.current) return threadRef.current;
		if (startingRef.current) return startingRef.current;
		if (!refRef.current) return null;
		// A refused start does not un-refuse itself in five seconds.
		if (Date.now() < startBlockedUntilRef.current) return null;

		const target = refRef.current;
		const flight = (async (): Promise<string | null> => {
			try {
				const thread = await openThread(target);

				// A 200 whose body is not the DTO would otherwise assign `undefined` here, making
				// every later `if (threadRef.current)` falsy and re-POSTing forever with no
				// exception anywhere. That is what a missing .env produces: Vite's history
				// fallback answers the relative URL with the SPA's own HTML at HTTP 200.
				const key = typeof thread?.key === 'string' && thread.key.length > 0 ? thread.key : null;
				if (!key) throw new Error('thread response carried no key');

				startAttemptsRef.current = 0;
				startBlockedUntilRef.current = 0;
				setReach('ok');
				threadRef.current = key;
				setThreadKey(key);
				return key;
			} catch (error) {
				const status = statusOf(error);
				setReach(reachFromStatus(status));

				const attempts = (startAttemptsRef.current += 1);
				startBlockedUntilRef.current = nextAttemptAt(attempts, Date.now());

				// console.error, not warn: only console.error is captured by console-buffer.ts,
				// which is what "Report a problem" ships to support. Status and attempt only —
				// never the response body, never a token.
				console.error('[support] could not open the thread', {
					thread: localThreadId(target),
					status: status ?? 'network',
					attempt: attempts,
				});
				return null;
			} finally {
				startingRef.current = null;
			}
		})();

		startingRef.current = flight;
		return flight;
	}, []);

	/** Fresh user intent clears the backoff — an agent tapping Retry means "now". */
	const clearStartBackoff = useCallback(() => {
		startAttemptsRef.current = 0;
		startBlockedUntilRef.current = 0;
	}, []);

	const retryThread = useCallback(() => {
		clearStartBackoff();
		void ensureThread();
	}, [clearStartBackoff, ensureThread]);

	const refresh = useCallback(async () => {
		const key = threadRef.current ?? (await ensureThread());
		if (!key) return;

		try {
			const { messages: fetched } = await fetchSupportMessages(key);
			setMessages((current) => {
				const merged = upsertMany(current, fetched);
				writeThreadCache(
					userId,
					key,
					merged.filter((m) => m.id)
				);
				return merged;
			});
		} catch (error) {
			// A failed poll is not an error state — the next tick tries again. A REFUSED one is:
			// a thread that was ours and no longer is (reassignment) has to say so.
			if (statusOf(error) === 403) setReach('forbidden');
		}
	}, [ensureThread]);

	/**
	 * Mark everything currently due as failed, because there is no thread to send it to.
	 *
	 * <p>Items are HELD, never deleted — the body is untouched, so Retry resends the original
	 * text under its original clientId and idempotency still holds.</p>
	 *
	 * <p>Attachment-orphaned items are skipped: `needsReattachment` is about a photo lost to a
	 * reload, not about the thread, and burning its attempts would push it to `held` while the
	 * agent is still being asked to pick the file again.</p>
	 */
	const markDueItemsFailed = useCallback(() => {
		if (!userId || !localId) return;
		const now = Date.now();

		for (const item of pendingItems(userId, localId)) {
			if (!isDue(item, now)) continue;
			if (needsReattachment(item, filesRef.current.has(item.clientId))) continue;

			const attempts = item.attempts + 1;
			upsertOutboxItem(userId, {
				...item,
				attempts,
				state: hasExhaustedAttempts(attempts) ? 'held' : 'failed',
				nextAttemptAt: nextAttemptAt(attempts, now),
			});
			setMessages((current) => upsertByClientIdThenId(current, { ...toMessage(item, null, me), state: 'failed' }));
		}
	}, [localId, me, userId]);

	/**
	 * Drain this thread's slice of the outbox.
	 *
	 * <p>Sequential with a mutex, mirroring usePulseChecklist's flushQueue: parallel sends
	 * from one device would arrive interleaved, and the whole point of the queue is that a
	 * burst composed offline reaches the other side in the order it was written.</p>
	 *
	 * <p>Filtered by `threadRef`, never the whole queue: the queue is per USER, and a message
	 * queued for one conversation must never be delivered to another because that one's thread
	 * resolved first.</p>
	 */
	const flush = useCallback(async () => {
		if (flushingRef.current || !userId || !localId) return;
		if (typeof navigator !== 'undefined' && !navigator.onLine) return;

		// Resolve the thread the same way refresh() already does. This line was the whole
		// delivery bug: flush() returned here on every 5s tick without ever trying to open a
		// thread, so a queued message sat on a clock forever with nothing reporting why.
		const key = threadRef.current ?? (await ensureThread());

		if (!key) {
			// Say so on the bubbles. "Queued" claims we are still trying; we are not, and the
			// agent needs to know their report is not moving so they can use the radio instead.
			markDueItemsFailed();
			return;
		}

		flushingRef.current = true;
		setSending(true);

		try {
			for (const item of pendingItems(userId, localId)) {
				if (!isDue(item, Date.now())) continue;

				// A queued item that carries attachment metadata but has no File lost its blob to
				// a reload. Sending it now would deliver a message the agent believes has a photo
				// attached and which silently does not -- worse than not sending, because they
				// would never know. It waits for a re-attach instead.
				if (needsReattachment(item, filesRef.current.has(item.clientId))) continue;

				try {
					upsertOutboxItem(userId, { ...item, state: 'sending' });

					let attachments;
					const file = filesRef.current.get(item.clientId);
					if (file) {
						const uploaded = await uploadSupportAttachment(key, file, (percent) =>
							setUploadPercents((current) => ({ ...current, [item.clientId]: percent }))
						);
						attachments = [uploaded];
					}

					const saved = await sendSupportMessage(key, {
						clientId: item.clientId,
						body: item.body,
						attachments,
						// Read off the persisted item, not from component state: this attempt may be
						// the first one after a reload, minutes or hours later.
						replyToId: item.replyToId,
					});

					removeOutboxItem(userId, item.clientId);
					filesRef.current.delete(item.clientId);
					setMessages((current) => upsertByClientIdThenId(current, saved));
				} catch (error) {
					if (statusOf(error) === 403) setReach('forbidden');

					const attempts = item.attempts + 1;
					const held = hasExhaustedAttempts(attempts);

					// HELD, never deleted. Discarding an agent's unsent message about a blocked
					// polling unit is not an acceptable failure mode.
					upsertOutboxItem(userId, {
						...item,
						attempts,
						state: held ? 'held' : 'failed',
						nextAttemptAt: nextAttemptAt(attempts, Date.now()),
					});

					setMessages((current) =>
						upsertByClientIdThenId(current, {
							...toMessage(item, key, me),
							state: 'failed',
						})
					);
				}
			}
		} finally {
			flushingRef.current = false;
			setSending(false);
		}
	}, [ensureThread, localId, markDueItemsFailed, me, userId]);

	// Keep the ref pointing at the latest refresh so the socket effects can call it without
	// depending on it.
	refreshRef.current = refresh;

	/**
	 * The badge signal.
	 *
	 * <p>Arrives on the agent's own user group, so it fires even when the panel has never been
	 * opened and before this thread has a key. Older servers send it bare; newer ones name the
	 * thread, and a signal for another conversation is not this engine's business.</p>
	 */
	useEffect(() => {
		if (!live || !localId) return;
		return live.on('SupportUnreadChanged', (payload) => {
			const key = threadKeyOf(payload);
			if (key && threadRef.current && key !== threadRef.current) return;
			void refreshRef.current?.();
		});
	}, [live, localId]);

	/** Everything scoped to this thread's group, filtered by key so a sibling's traffic is ignored. */
	useEffect(() => {
		if (!live || !threadKey) return;

		const unsubscribes = [
			live.onThread(threadKey, 'SupportMessageAdded', (payload) => {
				const raw = payload as Record<string, unknown>;
				// Mapped through the SAME normalizer the HTTP path uses, not a second copy of it. The
				// two ingest paths must agree on every field, and a hand-rolled copy here is how
				// senderSide came to be missing on socket-delivered rows once already — which made an
				// agent's own message render as if the support desk had sent it after any reload.
				setMessages((current) => upsertByClientIdThenId(current, normalizeMessage(raw, threadKey)));
				// A message arriving means the other side is no longer typing.
				setOtherIsTyping(false);
			}),

			live.onThread(threadKey, 'SupportMessageDeleted', (payload) => {
				const raw = payload as Record<string, unknown>;
				setMessages((current) =>
					current.map((m) =>
						m.id === raw.id
							? {
									...m,
									deletedAt: (raw.deletedAt as string) ?? new Date().toISOString(),
									deletedBy: raw.deletedBy as ISupportMessage['deletedBy'],
									body: '',
								}
							: m
					)
				);
			}),

			live.onThread(threadKey, 'SupportMessageEdited', (payload) => {
				const raw = payload as Record<string, unknown>;
				setMessages((current) =>
					current.map((m) =>
						m.id === raw.id ? { ...m, body: (raw.body as string) ?? m.body, editedAt: new Date().toISOString() } : m
					)
				);
			}),

			live.onThread(threadKey, 'SupportMessagesRead', (payload) => {
				const raw = payload as { upToSequence?: number; readBy?: string; readByCoreUserId?: number | null };
				const mark = raw.upToSequence ?? 0;
				// WHO read decides WHICH messages advance. The other side's receipt advances the
				// reader's own messages; the echo of the reader's own report advances the other
				// side's, which is what clears the badge now instead of on the next poll. Decided
				// on the reader's id when the server names one, and on the side only for a support
				// thread — `clientId` is not an ownership test, both halves mint one.
				const byMe = raw.readByCoreUserId != null ? raw.readByCoreUserId === me : raw.readBy === 'Agent' && kind === 'support';
				setMessages((current) =>
					current.map((m) =>
						m.sequence <= mark && m.state !== 'failed' && own(m) !== byMe
							? { ...m, state: 'read', readByOther: byMe ? m.readByOther : true }
							: m
					)
				);
			}),

			live.onThread(threadKey, 'UserTyping', (payload) => {
				const raw = payload as { isTyping?: boolean };
				typingReceivedAtRef.current = Date.now();
				setOtherIsTyping(!!raw.isTyping);
			}),
		];

		return () => unsubscribes.forEach((unsubscribe) => unsubscribe());
	}, [kind, live, me, own, threadKey]);

	/**
	 * Backfill on EVERY transition to connected, including reconnects. The socket does not
	 * replay what was missed while it was down, and a client that assumes it does ends up with
	 * a transcript that looks continuous and is not. The group re-join itself is the client's
	 * job — it replays every joined key on (re)connect.
	 */
	useEffect(() => {
		if (liveStatus !== 'connected' || !localId) return;
		void refreshRef.current?.();
	}, [liveStatus, localId]);

	/** Received indicators expire on their own; a sender that dies mid-type must not pin it. */
	useEffect(() => {
		if (!otherIsTyping) return;
		const id = window.setInterval(() => {
			if (hasTypingExpired(typingReceivedAtRef.current, Date.now())) setOtherIsTyping(false);
		}, 1000);
		return () => window.clearInterval(id);
	}, [otherIsTyping]);

	/** Join the thread as soon as it is known, and whenever it changes. Reference-counted by the client. */
	useEffect(() => {
		if (!live || !threadKey) return;
		void live.joinThread(threadKey);
		return () => {
			void live.leaveThread(threadKey);
		};
	}, [live, threadKey]);

	// Poll, at a cadence that reflects whether anyone is looking.
	useEffect(() => {
		if (!userId || !localId) return;
		void refresh();
		const cadence = liveStatus === 'connected' ? POLL_LIVE_MS : isActive ? POLL_OPEN_MS : POLL_CLOSED_MS;
		const id = window.setInterval(() => void refresh(), cadence);
		return () => window.clearInterval(id);
	}, [isActive, liveStatus, localId, refresh, userId]);

	// Flush on a timer AND on regaining connectivity — the online event is the one that
	// matters to an agent walking back into signal.
	useEffect(() => {
		if (!userId || !localId) return;
		const id = window.setInterval(() => void flush(), FLUSH_TICK_MS);
		const onOnline = () => {
			// Regaining signal changes the world — do not make the agent sit out a backoff
			// window that was earned while the radio was off.
			clearStartBackoff();
			void flush();
		};
		window.addEventListener('online', onOnline);
		return () => {
			window.clearInterval(id);
			window.removeEventListener('online', onOnline);
		};
	}, [clearStartBackoff, flush, localId, userId]);

	/** Close an open typing burst: tell the other side the keys have stopped. */
	const stopTyping = useCallback(() => {
		const key = threadRef.current;
		const client = liveRef.current;
		const hadBurst = lastTypingEmitRef.current !== null;
		lastTypingEmitRef.current = null;
		lastKeystrokeAtRef.current = null;
		setTypingBurst(false);
		if (hadBurst && key && client) void client.sendTyping(key, false);
	}, []);

	/**
	 * An explicit stop after the keys go quiet.
	 *
	 * <p>The receiver expires an indicator on its own after seven seconds, but that is the
	 * safety net for a sender that died mid-sentence, not the normal path: without a stop the
	 * other side reads "typing…" for seven seconds after every message that was composed and
	 * then abandoned.</p>
	 */
	useEffect(() => {
		if (!typingBurst) return;
		const id = window.setInterval(() => {
			if (shouldEmitStop(lastKeystrokeAtRef.current, Date.now())) stopTyping();
		}, TYPING_STOP_TICK_MS);
		return () => window.clearInterval(id);
	}, [stopTyping, typingBurst]);

	const send = useCallback(
		(body: string, file: File | null, replyTo?: ISupportReplyContext | null) => {
			if (!userId || !localId) return;

			// Sending ends the burst — the message itself says the typing is over.
			stopTyping();

			// Minted ONCE, replayed on every retry. This is the entire idempotency story.
			const clientId = mintClientId();
			ownClientIds.current.add(clientId);

			const item: IOutboxItem = {
				clientId,
				threadId: threadRef.current ?? '',
				threadRef: localId,
				body,
				createdAt: new Date().toISOString(),
				attempts: 0,
				nextAttemptAt: 0,
				state: 'queued',
				// Persisted with the item so a retry after a reload still answers the same message.
				replyToId: replyTo?.id,
				replyTo: replyTo ?? null,
			};

			const stage = async () => {
				if (file) {
					// Compress before anything else. 1600px/0.6MB turns a 4MB result sheet into
					// ~500KB — nine seconds on 2G instead of ninety.
					const compressed = await compressForUpload(file);
					filesRef.current.set(clientId, compressed);
					const url = URL.createObjectURL(compressed);
					previewsRef.current[clientId] = url;

					// A ~4KB JPEG data URL, small enough for localStorage. The FULL blob is never
					// persisted: storage is ~5MB and a 3MB photo base64s to ~4MB, which would blow
					// the quota and evict the queued TEXT messages alongside it. This is what lets
					// a reloaded tab still draw the bubble so the agent can see WHICH photo needs
					// re-picking, rather than being shown an anonymous broken row.
					item.attachment = {
						fileName: compressed.name,
						contentType: compressed.type,
						fileSizeBytes: compressed.size,
						// A PDF has no picture; its card is drawn from the name and size alone.
						thumbnailDataUrl: compressed.type.startsWith('image/') ? await buildThumbnailDataUrl(compressed) : undefined,
					};
				}

				try {
					upsertOutboxItem(userId, item);
					setStorageFull(false);
				} catch (error) {
					// Never silently drop. The composer surfaces this so the agent can act.
					if (error instanceof OutboxStorageFullError) setStorageFull(true);
				}

				setMessages((current) => upsertByClientIdThenId(current, toMessage(item, threadRef.current, me)));
				void ensureThread().then(() => flush());
			};

			void stage();
		},
		[ensureThread, flush, localId, me, stopTyping, userId]
	);

	/**
	 * Emit a typing event, throttled.
	 *
	 * Throttle rather than debounce: debounce fires after the user STOPS, so a fast typist
	 * mid-sentence emits nothing and the other side sees silence for exactly as long as
	 * someone is actively writing to them.
	 */
	const notifyTyping = useCallback(() => {
		const key = threadRef.current;
		const client = liveRef.current;
		if (!key || !client) return;
		if (!canEmitTyping(navigator.onLine, client.isConnected)) return;

		const now = Date.now();
		lastKeystrokeAtRef.current = now;

		const decision = shouldEmitTyping(lastTypingEmitRef.current, now);
		if (!decision.emit) return;

		lastTypingEmitRef.current = decision.lastEmittedAt;
		setTypingBurst(true);
		void client.sendTyping(key, true);
	}, []);

	const retry = useCallback(
		(clientId: string) => {
			if (!userId) return;
			const item = readOutbox(userId).find((i) => i.clientId === clientId);
			if (!item) return;
			upsertOutboxItem(userId, { ...item, state: 'queued', nextAttemptAt: 0 });
			clearStartBackoff();
			void flush();
		},
		[clearStartBackoff, flush, userId]
	);

	/**
	 * Re-attach a photo to a message whose blob was lost to a reload.
	 *
	 * Keyed on the ORIGINAL clientId, so the message keeps its position in the queue and its
	 * idempotency key — a re-attach is not a new message.
	 */
	const reattach = useCallback(
		(clientId: string, file: File) => {
			if (!userId) return;

			void (async () => {
				const compressed = await compressForUpload(file);
				filesRef.current.set(clientId, compressed);
				previewsRef.current[clientId] = URL.createObjectURL(compressed);

				const item = readOutbox(userId).find((i) => i.clientId === clientId);
				if (!item) return;

				upsertOutboxItem(userId, {
					...item,
					state: 'queued',
					nextAttemptAt: 0,
					attachment: {
						fileName: compressed.name,
						contentType: compressed.type,
						fileSizeBytes: compressed.size,
						// A PDF has no picture; its card is drawn from the name and size alone.
						thumbnailDataUrl: compressed.type.startsWith('image/') ? await buildThumbnailDataUrl(compressed) : undefined,
					},
				});

				void flush();
			})();
		},
		[flush, userId]
	);

	/**
	 * Edit and delete are NOT optimistic, deliberately.
	 *
	 * Optimistic SEND is essential — it is the entire perceived-latency win. Optimistic
	 * edit/delete is not: the user has already seen their text, so there is nothing to hide,
	 * and an optimistic delete that rolls back is a message RESURRECTING itself. On a
	 * retraction that reads as a privacy failure rather than a glitch.
	 */
	const reportRead = useCallback((upToSequence: number) => {
		const key = threadRef.current;
		if (!key || upToSequence <= 0) return;
		// Fire-and-forget: a failed read report is not worth surfacing, and the next dwell
		// re-reports a mark at least as high.
		void markSupportRead(key, upToSequence).catch(() => undefined);
	}, []);

	const editMessage = useCallback(async (messageId: string, body: string): Promise<string | null> => {
		try {
			await editSupportMessage(messageId, body);
			setMessages((current) => current.map((m) => (m.id === messageId ? { ...m, body, editedAt: new Date().toISOString() } : m)));
			return null;
		} catch (error) {
			const status = statusOf(error);
			// 409 is the server's window/read refusal. Its own copy is more specific than
			// anything we could infer here, so surface the distinction rather than flattening
			// every failure into "try again".
			if (status === 409) return 'This message is too old to edit, or has already been read. Send a correction instead.';
			return 'Couldn’t edit that. Check your connection and try again.';
		}
	}, []);

	const deleteMessage = useCallback(async (messageId: string): Promise<string | null> => {
		try {
			await deleteSupportMessage(messageId);
			setMessages((current) =>
				current.map((m) => (m.id === messageId ? { ...m, body: '', deletedAt: new Date().toISOString(), deletedBy: 'self' } : m))
			);
			return null;
		} catch (error) {
			const status = statusOf(error);
			if (status === 409) return 'This message has been escalated to an incident and can no longer be deleted.';
			return 'Couldn’t delete that. Check your connection and try again.';
		}
	}, []);

	// Object URLs are revoked only on unmount: revoking when a send settles would blank the
	// bubble the agent is still looking at.
	useEffect(
		() => () => {
			Object.values(previewsRef.current).forEach((url) => URL.revokeObjectURL(url));
		},
		[]
	);

	// Counted on ownership, NOT on "has no clientId".
	//
	// The desk mints a clientId on every send too, and the server persists and echoes it — so
	// every desk message carried one and the old predicate excluded all of them. The badge was
	// therefore structurally always 0 for exactly the messages it exists to count. Same
	// precedence as isOwnMessage: the badge must not use a weaker test than the transcript.
	const unread = messages.filter((m) => !own(m) && m.state !== 'read').length;

	// Recomputed each render from this thread's slice of the outbox and the in-memory File
	// map. Cheap: the outbox holds a handful of items at most, capped by MAX_PENDING_ATTACHMENTS.
	const mine = userId && localId ? itemsForThread(userId, localId) : [];
	const needsReattach = new Set(
		mine.filter((i) => needsReattachment(i, filesRef.current.has(i.clientId))).map((i) => i.clientId)
	);

	return {
		kind,
		localId,
		me,
		threadKey,
		messages,
		unread,
		online,
		liveStatus,
		otherIsTyping,
		notifyTyping,
		sending,
		storageFull,
		localPreviews: previewsRef.current,
		uploadPercents,
		ownClientIds: ownClientIds.current,
		open: () => {
			// Opening the panel is user intent: do not make them wait out a backoff window.
			clearStartBackoff();
			void ensureThread();
		},
		close: () => undefined,
		send,
		retry,
		reach,
		retryThread,
		composerDisabled: kind === 'peer' && reach === 'forbidden',
		needsReattach,
		reattach,
		reportRead,
		editMessage,
		deleteMessage,
		thumbnails: Object.fromEntries(
			mine.filter((i) => i.attachment?.thumbnailDataUrl).map((i) => [i.clientId, i.attachment!.thumbnailDataUrl!])
		),
		pendingAttachmentCount: mine.filter((i) => i.attachment).length,
	};
};

/** Optimistic view of a queued item, so it renders before the network has seen it. */
const toMessage = (item: IOutboxItem, threadKey: string | null, me: number): ISupportMessage => ({
	clientId: item.clientId,
	threadId: threadKey ?? item.threadId,
	// Stamped as the reader's own: ownership is decided on the sender id first, and a row this
	// device composed has exactly one possible sender.
	senderId: me,
	// Client-only rows sort last: they have no server sequence, and Number.MAX_SAFE_INTEGER
	// keeps them pinned to the bottom without ever being mistaken for a real sequence by
	// highestSequence(), which filters on the presence of a server id.
	sequence: Number.MAX_SAFE_INTEGER,
	kind: item.attachment ? (isDocumentAttachment(item.attachment) ? 'document' : 'image') : 'text',
	body: item.body,
	// What is being sent, so the bubble can tell a PDF (file card) from a photo before the echo.
	attachments: item.attachment
		? [
				{
					fileName: item.attachment.fileName,
					contentType: item.attachment.contentType,
					fileSizeBytes: item.attachment.fileSizeBytes,
				},
			]
		: undefined,
	state: item.state === 'failed' ? 'failed' : item.state === 'sending' ? 'sending' : 'queued',
	createdAt: item.createdAt,
	// So the quote is on screen with the message rather than appearing a beat later when the
	// server echo lands. The echo carries the server's own resolved copy and replaces this one.
	replyToId: item.replyToId ?? null,
	replyTo: item.replyTo ?? null,
});

const outboxToMessages = (items: IOutboxItem[], threadKey: string | null, me: number): ISupportMessage[] =>
	items.map((item) => toMessage(item, threadKey, me));
